import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  TrophyIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  StarIcon,
  FireIcon,
  SparklesIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

interface AnalyticsData {
  savingsTrends: {
    month: string;
    totalSavings: number;
    googleAds: number;
    microsoft: number;
    aws: number;
    aramex: number;
  }[];
  categoryBreakdown: {
    name: string;
    nameAr: string;
    value: number;
    color: string;
  }[];
  performanceMetrics: {
    metric: string;
    metricAr: string;
    current: number;
    target: number;
    trend: 'up' | 'down' | 'stable';
    change: number;
  }[];
  roiAnalysis: {
    partner: string;
    partnerAr: string;
    investment: number;
    returns: number;
    roi: number;
    efficiency: number;
  }[];
}

interface PartnershipsAnalyticsProps {
  className?: string;
}

const PartnershipsAnalytics: React.FC<PartnershipsAnalyticsProps> = ({ className = '' }) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'trends' | 'breakdown' | 'performance' | 'roi'>('trends');

  useEffect(() => {
    const generateAnalyticsData = (): AnalyticsData => {
      return {
        savingsTrends: [
          {
            month: 'يناير',
            totalSavings: 45000,
            googleAds: 15000,
            microsoft: 12000,
            aws: 10000,
            aramex: 8000
          },
          {
            month: 'فبراير',
            totalSavings: 62000,
            googleAds: 22000,
            microsoft: 15000,
            aws: 14000,
            aramex: 11000
          },
          {
            month: 'مارس',
            totalSavings: 78000,
            googleAds: 28000,
            microsoft: 18000,
            aws: 19000,
            aramex: 13000
          },
          {
            month: 'أبريل',
            totalSavings: 95000,
            googleAds: 35000,
            microsoft: 22000,
            aws: 24000,
            aramex: 14000
          },
          {
            month: 'مايو',
            totalSavings: 112000,
            googleAds: 42000,
            microsoft: 26000,
            aws: 28000,
            aramex: 16000
          },
          {
            month: 'يونيو',
            totalSavings: 134000,
            googleAds: 48000,
            microsoft: 31000,
            aws: 35000,
            aramex: 20000
          }
        ],
        categoryBreakdown: [
          { name: 'Marketing', nameAr: 'التسويق', value: 45, color: '#3b82f6' },
          { name: 'Software', nameAr: 'البرمجيات', value: 25, color: '#10b981' },
          { name: 'Cloud', nameAr: 'السحابة', value: 20, color: '#f59e0b' },
          { name: 'Logistics', nameAr: 'اللوجستيات', value: 10, color: '#ef4444' }
        ],
        performanceMetrics: [
          {
            metric: 'Total Savings',
            metricAr: 'إجمالي التوفير',
            current: 134000,
            target: 150000,
            trend: 'up',
            change: 23.5
          },
          {
            metric: 'Active Partnerships',
            metricAr: 'الشراكات النشطة',
            current: 12,
            target: 15,
            trend: 'up',
            change: 15.2
          },
          {
            metric: 'Average ROI',
            metricAr: 'متوسط العائد',
            current: 285,
            target: 300,
            trend: 'up',
            change: 8.7
          },
          {
            metric: 'Partner Satisfaction',
            metricAr: 'رضا الشركاء',
            current: 4.6,
            target: 4.8,
            trend: 'stable',
            change: 2.1
          }
        ],
        roiAnalysis: [
          {
            partner: 'Google Ads',
            partnerAr: 'إعلانات جوجل',
            investment: 25000,
            returns: 95000,
            roi: 380,
            efficiency: 94
          },
          {
            partner: 'AWS Cloud',
            partnerAr: 'أمازون ويب سيرفيسز',
            investment: 30000,
            returns: 85000,
            roi: 283,
            efficiency: 89
          },
          {
            partner: 'Microsoft 365',
            partnerAr: 'مايكروسوفت 365',
            investment: 20000,
            returns: 65000,
            roi: 325,
            efficiency: 87
          },
          {
            partner: 'Aramex',
            partnerAr: 'أرامكس',
            investment: 15000,
            returns: 42000,
            roi: 280,
            efficiency: 82
          }
        ]
      };
    };

    setTimeout(() => {
      setAnalyticsData(generateAnalyticsData());
      setLoading(false);
    }, 800);
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />;
      case 'down':
        return <ArrowTrendingDownIcon className="w-5 h-5 text-red-600" />;
      default:
        return <div className="w-5 h-5 bg-gray-400 rounded-full" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading || !analyticsData) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"
          />
          <span className="mr-3 text-gray-600 dark:text-gray-400">
            جاري تحليل بيانات الشراكات...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <PresentationChartLineIcon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">تحليلات الشراكات المتقدمة</h2>
              <p className="text-blue-100">رؤى عميقة مدعومة بالذكاء الاصطناعي</p>
            </div>
          </div>
          <div className="text-white text-right">
            <div className="text-3xl font-bold">{formatCurrency(134000)}</div>
            <div className="text-sm opacity-75">إجمالي التوفير الشهري</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8 rtl:space-x-reverse px-6">
          {[
            { id: 'trends', label: 'اتجاهات التوفير', icon: ArrowTrendingUpIcon },
            { id: 'breakdown', label: 'تحليل الفئات', icon: ChartBarIcon },
            { id: 'performance', label: 'مؤشرات الأداء', icon: TrophyIcon },
            { id: 'roi', label: 'تحليل العائد', icon: CurrencyDollarIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 rtl:space-x-reverse py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'trends' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                اتجاهات التوفير عبر الشركاء
              </h3>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analyticsData.savingsTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="googleAds"
                      stackId="1"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      name="إعلانات جوجل"
                    />
                    <Area
                      type="monotone"
                      dataKey="microsoft"
                      stackId="1"
                      stroke="#10b981"
                      fill="#10b981"
                      name="مايكروسوفت"
                    />
                    <Area
                      type="monotone"
                      dataKey="aws"
                      stackId="1"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      name="أمازون"
                    />
                    <Area
                      type="monotone"
                      dataKey="aramex"
                      stackId="1"
                      stroke="#ef4444"
                      fill="#ef4444"
                      name="أرامكس"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'breakdown' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                توزيع التوفير حسب الفئة
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={analyticsData.categoryBreakdown}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ nameAr, value }) => `${nameAr}: ${value}%`}
                      >
                        {analyticsData.categoryBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4">
                  {analyticsData.categoryBreakdown.map((category, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {category.nameAr}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {category.value}%
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatCurrency((category.value / 100) * 134000)}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                مؤشرات الأداء الرئيسية
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {analyticsData.performanceMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {metric.metricAr}
                      </h4>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        {getTrendIcon(metric.trend)}
                        <span className={`text-sm font-medium ${getTrendColor(metric.trend)}`}>
                          {metric.change > 0 ? '+' : ''}{metric.change}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">الحالي</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {metric.metricAr.includes('التوفير') ? formatCurrency(metric.current) : 
                           metric.metricAr.includes('العائد') ? `${metric.current}%` :
                           metric.metricAr.includes('رضا') ? `${metric.current}/5` : metric.current}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">الهدف</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {metric.metricAr.includes('التوفير') ? formatCurrency(metric.target) : 
                           metric.metricAr.includes('العائد') ? `${metric.target}%` :
                           metric.metricAr.includes('رضا') ? `${metric.target}/5` : metric.target}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min((metric.current / metric.target) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'roi' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                تحليل العائد على الاستثمار
              </h3>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData.roiAnalysis}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="partnerAr" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'roi' ? `${value}%` : formatCurrency(value as number),
                        name === 'roi' ? 'العائد' : name === 'investment' ? 'الاستثمار' : 'العوائد'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="investment" fill="#ef4444" name="الاستثمار" />
                    <Bar dataKey="returns" fill="#10b981" name="العوائد" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {analyticsData.roiAnalysis.map((partner, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                  >
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                      {partner.partnerAr}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">الاستثمار</span>
                        <span className="font-medium text-red-600">{formatCurrency(partner.investment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">العوائد</span>
                        <span className="font-medium text-green-600">{formatCurrency(partner.returns)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">العائد</span>
                        <span className="font-bold text-blue-600">{partner.roi}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">الكفاءة</span>
                        <span className="font-medium text-purple-600">{partner.efficiency}%</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnershipsAnalytics;
